
import React, { useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EventFormValues } from '../schemas/eventFormSchema';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, GraduationCap } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { loadProfessions } from '@/services/professionService';
import { Profession } from '@/lib/professionTypes';
import ProfessionList from './ProfessionList';
import SelectedProfessionBadges from './SelectedProfessionBadges';

interface ProfessionSelectorProps {
  form: UseFormReturn<EventFormValues>;
}

const ProfessionSelector = ({ form }: ProfessionSelectorProps) => {
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  // Szakmák betöltése
  useEffect(() => {
    const fetchProfessions = async () => {
      try {
        const data = await loadProfessions();
        setProfessions(data);
      } catch (error) {
        console.error('Error loading professions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfessions();
  }, []);

  // Szűrt szakmák listája a keresés alapján
  const filteredProfessions = searchTerm 
    ? professions.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        p.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : professions;
  
  // Kategória filter
  const professionsToShow = activeTab === 'all' 
    ? filteredProfessions 
    : filteredProfessions.filter(p => p.category.toLowerCase() === activeTab.toLowerCase());

  // Kiválasztott szakmák
  const selectedProfessions = form.watch('professions') || [];

  // Szakma kiválasztás kezelése
  const handleProfessionChange = (professionId: string, checked: boolean) => {
    const currentSelection = form.getValues('professions') || [];
    if (checked) {
      form.setValue('professions', [...currentSelection, professionId]);
    } else {
      form.setValue(
        'professions', 
        currentSelection.filter(id => id !== professionId)
      );
    }
  };

  // Szakma eltávolítása
  const handleRemoveProfession = (professionId: string) => {
    const currentSelection = form.getValues('professions') || [];
    form.setValue(
      'professions', 
      currentSelection.filter(id => id !== professionId)
    );
  };

  return (
    <Card className="border-2 border-medihub-primary/20">
      <CardHeader className="bg-medihub-primary/10 pb-2">
        <CardTitle className="text-lg flex items-center gap-2 text-medihub-primary">
          <GraduationCap className="h-5 w-5" />
          Célközönség szakmák szerint
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <FormDescription>
          Válassza ki, mely szakterületek képviselőinek szól az esemény. Ez segíti a célközönséget az esemény megtalálásában.
        </FormDescription>
        
        <div className="space-y-4">
          {/* Keresőmező */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Keressen szakterület szerint..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {/* Már kiválasztott szakmák megjelenítése */}
          <SelectedProfessionBadges 
            selectedProfessions={selectedProfessions}
            professions={professions}
            onRemoveProfession={handleRemoveProfession}
          />
          
          {/* Kategória fülek */}
          <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full">
              <TabsTrigger value="all" className="flex-1">Összes</TabsTrigger>
              <TabsTrigger value="orvos" className="flex-1">Orvosok</TabsTrigger>
              <TabsTrigger value="szakdolgozó" className="flex-1">Szakdolgozók</TabsTrigger>
              <TabsTrigger value="szakfogorvos" className="flex-1">Fogorvosok</TabsTrigger>
            </TabsList>
          </Tabs>
          
          {/* Szakmák listája */}
          <FormField
            control={form.control}
            name="professions"
            render={() => (
              <FormItem>
                <FormControl>
                  <ProfessionList 
                    loading={loading}
                    professions={professionsToShow}
                    selectedProfessions={selectedProfessions}
                    onProfessionChange={handleProfessionChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfessionSelector;
